// client/src/branding/index.ts
// Tenant branding registry and hostname-based resolver.
//
// Resolution order:
//   1. Exact hostname match (case-insensitive)
//   2. Subdomain prefix match (e.g. 'icaros.foo.com' matches tenant domain 'icaros.agoc-school.com'
//      by comparing the first subdomain label)
//   3. Fallback: agoc (neutral default)

import type { TenantBranding } from './types';
import { agocBranding } from './agoc';
import { icarosBranding } from './icaros';

export type { TenantBranding };

/**
 * All registered tenant brandings.
 * To add a new tenant: create src/branding/<tenantId>.ts and add it here.
 */
export const brandingRegistry: readonly TenantBranding[] = [
  agocBranding,
  icarosBranding,
];

/**
 * Resolve the correct tenant branding from a hostname string.
 *
 * In development, the VITE_TENANT_OVERRIDE env var takes priority.
 * Set it via npm scripts (dev:agoc / dev:icaros) or in .env.local.
 *
 * @param hostname - raw hostname from window.location.hostname
 * @returns the matching TenantBranding, or agocBranding as fallback
 */
export function resolveTenantFromHostname(hostname: string): TenantBranding {
  // Dev-only env override — VITE_TENANT_OVERRIDE=icaros npm run dev
  if (import.meta.env.DEV && import.meta.env.VITE_TENANT_OVERRIDE) {
    const override = (import.meta.env.VITE_TENANT_OVERRIDE as string).toLowerCase().trim();
    const found = brandingRegistry.find(b => b.id === override);
    if (found) return found;
  }

  const normalised = hostname.trim().toLowerCase();

  // 1. Exact match
  for (const branding of brandingRegistry) {
    for (const domain of branding.domains) {
      if (domain.toLowerCase() === normalised) {
        return branding;
      }
    }
  }

  // 2. Subdomain prefix match:
  //    If the first subdomain label of the request hostname matches the first
  //    subdomain label of a registered domain, treat it as a match.
  //    e.g. 'icaros.internal.corp' matches domain 'icaros.agoc-school.com'
  //         because both start with 'icaros.'
  const requestLabel = normalised.split('.')[0] ?? '';
  if (requestLabel) {
    for (const branding of brandingRegistry) {
      for (const domain of branding.domains) {
        const domainLabel = domain.toLowerCase().split('.')[0] ?? '';
        if (domainLabel && domainLabel === requestLabel && domainLabel !== 'localhost') {
          return branding;
        }
      }
    }
  }

  // 3. Fallback
  return agocBranding;
}
